/**
 * Location Detection Utilities
 * Detects user location using IP geolocation (no permission required)
 * v10.0.2 - 2026-03-11 15:00 - IP-only, no GPS prompts
 */

export interface LocationInfo {
  city?: string;
  state?: string;
  country?: string;
  countryCode?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  isp?: string;
  ip?: string;
}

/**
 * Get location from IP address using ipapi.co (free tier: 1000 requests/day)
 */
export async function getLocationFromIP(): Promise<LocationInfo> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
    
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      },
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error('Failed to fetch location from IP');
    }

    const data = await response.json();
    
    return {
      city: data.city,
      state: data.region,
      country: data.country_name,
      countryCode: data.country_code,
      latitude: data.latitude,
      longitude: data.longitude,
      timezone: data.timezone,
      isp: data.org,
      ip: data.ip
    };
  } catch (error) {
    // Silently use default location without console warning to reduce noise
    console.log('📍 Using default Nigeria location (IP geolocation unavailable)');
    
    // Return default Nigerian location for Sentlo
    return {
      city: 'Lagos',
      state: 'Lagos',
      country: 'Nigeria',
      countryCode: 'NG',
      latitude: 6.5244,
      longitude: 3.3792,
      timezone: 'Africa/Lagos'
    };
  }
}

/**
 * Get user location with fallback strategy
 * Uses IP-based geolocation (no browser permission required)
 * This function NEVER throws errors, always returns a valid location
 */
export async function getUserLocation(useBrowserLocation = false): Promise<LocationInfo> {
  try {
    console.log('🌍 Detecting user location via IP...');
    
    // Use IP-based location (no permission required!)
    const ipLocation = await getLocationFromIP();
    console.log('📍 Location from IP:', ipLocation);
    return ipLocation;
  } catch (error) {
    // Final safety net - return default location if IP lookup fails
    console.log('📍 IP location failed, using default Nigeria location');
    return {
      city: 'Lagos',
      state: 'Lagos',
      country: 'Nigeria',
      countryCode: 'NG',
      latitude: 6.5244,
      longitude: 3.3792,
      timezone: 'Africa/Lagos'
    };
  }
}

/**
 * Format location for display
 */
export function formatLocation(location: LocationInfo): string {
  const parts: string[] = [];
  
  if (location.city && location.city !== 'Unknown') {
    parts.push(location.city);
  }
  
  if (location.state && location.state !== 'Unknown') {
    parts.push(location.state);
  }
  
  if (location.country && location.country !== 'Unknown') {
    parts.push(location.country);
  }
  
  return parts.length > 0 ? parts.join(', ') : 'Location Unknown';
}

/**
 * Get location emoji/flag based on country code
 */
export function getLocationEmoji(countryCode?: string): string {
  if (!countryCode) return '🌍';
  
  const emojiMap: Record<string, string> = {
    'NG': '🇳🇬', // Nigeria
    'US': '🇺🇸', // USA
    'GB': '🇬🇧', // UK
    'GH': '🇬🇭', // Ghana
    'KE': '🇰🇪', // Kenya
    'ZA': '🇿🇦', // South Africa
    'CA': '🇨🇦', // Canada
    'AU': '🇦🇺', // Australia
  };
  
  return emojiMap[countryCode.toUpperCase()] || '🌍';
}